import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useTheme } from 'styled-components';
import {
  PageShell,
  PageHeader,
  DataTable,
  HeaderLabel,
  IconTile,
  Icon,
  DateCard,
  eventDateCardProps,
  SalesTimeline,
  StatStrip,
  StatTile,
  useIsMobile,
  tokens,
  orange,
} from 'components/v3';
import { BoxOfficeReportPrint } from './BoxOfficeReport/BoxOfficeReportPrint';
import { useGetBoxOfficeReport } from './gql/mutation/useGetBoxOfficeReport.mutation';

// Box Office Report (all sellers) — gen-3 redesign. An event-wide stat strip,
// then one section per user: a Sales Summary card beside the ticket-breakdown
// table (two columns on desktop, stacked on narrow screens). Composed from v3
// components and tokens.

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

// Currency strings from the API ("$1,234.00") -> number.
const parseMoney = (value) =>
  Number(String(value || '').replace(/[^0-9.-]+/g, '')) || 0;

const BREAKDOWN_COLUMNS = [
  {
    key: 'name',
    label: 'Ticket Name',
    width: 'minmax(180px,2fr)',
    primary: true,
  },
  { key: 'qty', label: 'Quantity', width: '110px' },
  { key: 'price', label: 'Price', width: '120px' },
  { key: 'total', label: 'Total', width: '120px', align: 'right' },
];

// One label/value line in the Sales Summary card.
const SalesRow = ({ label, value, last }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        padding: '13px 0',
        borderBottom: last ? 'none' : '1px solid ' + t.line,
      }}
    >
      <span style={{ fontSize: 13.5, fontWeight: 600, color: t.sub }}>
        {label}
      </span>
      <span
        style={{
          fontSize: 15.5,
          fontWeight: 700,
          color: t.text,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
};

// Left column — the seller's sales totals as clean key/value rows, with the
// Total Revenue emphasized at the bottom.
const SalesCard = ({ seller }) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowCard,
        padding: '20px 22px',
      }}
    >
      <HeaderLabel>Sales Summary</HeaderLabel>
      <div style={{ marginTop: 6 }}>
        <SalesRow label="Cash" value={seller.cash} />
        <SalesRow label="Card" value={seller.card} />
        <SalesRow label="Transactions" value={seller.num_transactions} />
        <SalesRow label="Tickets" value={seller.num_tickets} last />
      </div>
      <div
        style={{
          marginTop: 16,
          padding: '16px 18px',
          background: orange(0.1),
          border: '1px solid ' + orange(0.32),
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.7px',
            textTransform: 'uppercase',
            color: t.primary,
          }}
        >
          Total Revenue
        </span>
        <span
          style={{
            fontSize: 24,
            fontWeight: 800,
            letterSpacing: '-0.5px',
            color: t.primary,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {seller.total_revenue}
        </span>
      </div>
    </div>
  );
};

// Seller identity header that spans both columns.
const SellerHeader = ({ seller }) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 13,
        marginBottom: 14,
      }}
    >
      <IconTile icon="user" size={42} iconSize={22} />
      <div
        style={{
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 800, color: t.text }}>
          {seller?.user?.formatted_name || 'Unknown User'}
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: t.sub }}>
          {seller.num_tickets} tickets · {seller.num_transactions} transactions
        </div>
      </div>
    </div>
  );
};

// Event context card (logo + name + track/series + date).
const EventCard = ({ event }) => {
  const t = tokens(useTheme());
  if (!event) return null;
  const trackOrSeries =
    event.track?.name ||
    (event.series && event.series.length ? event.series[0].name : '');
  const dateProps = eventDateCardProps(event);
  return (
    <div
      style={{
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowCard,
        padding: '18px 22px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 18,
      }}
    >
      {dateProps ? <DateCard {...dateProps} /> : null}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: t.text }}>
          {event.name}
        </div>
        {trackOrSeries ? (
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 500,
              color: t.sub,
              marginTop: 3,
            }}
          >
            {trackOrSeries}
          </div>
        ) : null}
      </div>
      {event.logo ? (
        <img
          src={event.logo}
          alt={event.name || ''}
          style={{
            width: 96,
            height: 60,
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
      ) : null}
    </div>
  );
};

// Orange primary "Export PDF" button (handed to BoxOfficeReportPrint's render slot).
const ExportPdfButton = () => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 9,
      height: 46,
      padding: '0 22px',
      borderRadius: 11,
      background: '#fa4616',
      color: '#fff',
      fontSize: 14.5,
      fontWeight: 700,
      boxShadow: '0 6px 16px rgba(250,70,22,0.28)',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    }}
  >
    <Icon name="download" size={18} color="#fff" />
    Export PDF
  </span>
);

// One seller block: identity header + two-column (Sales | Breakdown) body.
const SellerSection = ({ seller, renderRow }) => {
  const narrow = useIsMobile(1024);
  return (
    <div style={{ marginBottom: 28 }}>
      <SellerHeader seller={seller} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr' : '340px 1fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <SalesCard seller={seller} />
        <DataTable
          columns={BREAKDOWN_COLUMNS}
          data={seller.ticket_breakdown_data || []}
          renderRow={renderRow}
          emptyLabel="No tickets sold."
          title="Ticket Breakdown"
        />
      </div>
    </div>
  );
};

export const BoxOfficeReport = () => {
  const [boxOfficeReport, setBoxOfficeReport] = useState();
  const { id } = useParams();

  const location = useLocation();
  const { event } = location.state || {};

  const getBoxOfficeReport = useGetBoxOfficeReport();

  const eventData = event || boxOfficeReport?.event;
  const sellers = boxOfficeReport?.data || [];
  const timeline = boxOfficeReport?.timeline || [];
  const loading = !boxOfficeReport;

  useEffect(() => {
    const getData = async () => {
      const response = await getBoxOfficeReport(id);
      setBoxOfficeReport(response.data?.getPOSRegistersReport);
    };
    getData();
  }, [getBoxOfficeReport, id]);

  const totals = sellers.reduce(
    (acc, s) => ({
      cash: acc.cash + parseMoney(s.cash),
      card: acc.card + parseMoney(s.card),
      revenue: acc.revenue + parseMoney(s.total_revenue),
      tickets: acc.tickets + (s.num_tickets || 0),
      transactions: acc.transactions + (s.num_transactions || 0),
    }),
    { cash: 0, card: 0, revenue: 0, tickets: 0, transactions: 0 }
  );

  // Legend/colour source for the timeline — id + name per seller, in the same
  // order the seller sections render, so colours stay stable across the page.
  const timelineSellers = sellers
    .filter((s) => s?.user?.id)
    .map((s) => ({
      id: s.user.id,
      name: s.user.formatted_name || 'Unknown User',
    }));

  const renderRow = (item) => ({
    name: item.name,
    qty: item.qty,
    price: item.price,
    total: item.total,
  });

  return (
    <PageShell>
      <PageHeader icon="file-text" title="Box Office Report">
        {!loading && sellers.length ? (
          <BoxOfficeReportPrint
            eventId={id}
            date={eventData?.start_date}
            reportData={boxOfficeReport}
            button={<ExportPdfButton />}
          />
        ) : null}
      </PageHeader>

      <EventCard event={eventData} />

      {loading ? (
        <DataTable
          columns={BREAKDOWN_COLUMNS}
          data={[]}
          renderRow={renderRow}
          loading
        />
      ) : sellers.length ? (
        <>
          <StatStrip style={{ marginBottom: 24 }}>
            <StatTile label="Total Cash" value={currency.format(totals.cash)} />
            <StatTile label="Total Card" value={currency.format(totals.card)} />
            <StatTile
              label="Total POS Revenue"
              value={currency.format(totals.revenue)}
              accent
            />
            <StatTile label="Tickets Sold" value={totals.tickets} />
            <StatTile label="Transactions" value={totals.transactions} />
          </StatStrip>

          <SalesTimeline data={timeline} sellers={timelineSellers} />

          {sellers.map((seller, index) => (
            <SellerSection key={index} seller={seller} renderRow={renderRow} />
          ))}
        </>
      ) : (
        <DataTable
          columns={BREAKDOWN_COLUMNS}
          data={[]}
          renderRow={renderRow}
          emptyLabel="No box office sales for this event."
        />
      )}
    </PageShell>
  );
};
